// src/arena/useArenaVoice.js
// ══════════════════════════════════════════════════════════════════════════════
// 🥊 LA VOZ DE LA ARENA — el micrófono y el altavoz de una misión.
//
// Esto NO se parece al oído en vivo de Spacy y no comparte nada con él, a
// propósito. Allí se graba, se manda y se lee el texto: una dirección. Aquí hay
// dos personas hablando a la vez, y eso cambia todo el aparato.
//
// ── POR QUÉ NO SIRVE MediaRecorder ────────────────────────────────────────────
// El Voice Agent de Deepgram pide `linear16` a 16 kHz — PCM crudo. MediaRecorder
// entrega WebM/Opus, que es otro mundo: contenedor, cabeceras y compresión. No
// se puede configurar, así que la captura va por AudioWorklet, que es lo único
// que da acceso a las muestras tal cual.
//
// El worklet vive en un Blob y no en un archivo suelto. Un `.js` de worklet
// tiene que servirse como módulo aparte y eso depende de cómo empaquete Vite en
// cada entorno; el Blob se construye en memoria y funciona igual en desarrollo
// que en producción, sin tocar la configuración de nadie.
//
// ── LA CANCELACIÓN DE ECO NO ES OPCIONAL ──────────────────────────────────────
// El personaje sale por el altavoz y el micro lo vuelve a coger. Sin
// `echoCancellation` el agente se oye a sí mismo, cree que el alumno está
// hablando y se interrumpe solo en bucle. Con auriculares el problema
// desaparece del todo, y por eso la pantalla lo sugiere.
//
// ── LA INTERRUPCIÓN ES LA FUNCIÓN, NO UN EXTRA ────────────────────────────────
// Cuando Deepgram avisa de `UserStartedSpeaking` se corta en seco lo que estaba
// sonando. Poder cortar a alguien a media frase es lo que separa una
// conversación de dos monólogos por turnos, y es justo lo que el alumno tiene
// que aprender a hacer en inglés.
// ══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';
// ── EN LA ARENA, EL SILENCIO ES TODO ─────────────────────────────────────────
// Son dos minutos de conversacion con una persona. Un pitido de interfaz encima
// de la voz del personaje rompe exactamente lo que la Arena vende: que por un
// momento no parezca una app.
import { vozEnCurso } from '../audio/fx';

const ENTRADA_HZ = 16000;         // lo que pide el agente
const SALIDA_HZ = 24000;          // lo que devuelve
const MUESTRAS_POR_ENVIO = 1600;  // 100 ms — barato de mandar, rápido de cortar
const COLCHON_S = 0.06;           // margen de planificación del altavoz

const WS_UNAUTHORIZED = 4001;
const WS_POLICY = 4008;
const WS_UPSTREAM = 4010;

// ── DE DÓNDE SALE LA URL ──────────────────────────────────────────────────────
// Mismo criterio que el oído en vivo, y por el mismo motivo: las reescrituras de
// vercel.json no negocian el upgrade a WebSocket, así que en producción hay que
// apuntar al backend directo. Se prueban en cascada, nunca a la vez: dos
// conexiones serían dos misiones facturadas.
function urlsDelSocket() {
    const token = localStorage.getItem('token') || '';
    const cola = `/api/arena/enter?token=${encodeURIComponent(token)}`;
    const base = import.meta.env.VITE_API_URL || '';

    const protocolo = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const mismoOrigen = `${protocolo}//${window.location.host}${cola}`;

    if (!/^https?:\/\//i.test(base)) return [mismoOrigen];

    const directo = base.replace(/^http/i, 'ws').replace(/\/+$/, '') + cola;
    return directo === mismoOrigen ? [directo] : [directo, mismoOrigen];
}

function describirCierre(codigo) {
    if (codigo === WS_UNAUTHORIZED) return 'Tu sesión caducó. Vuelve a entrar.';
    if (codigo === WS_POLICY) return 'Espera un momento antes de otra misión.';
    if (codigo === WS_UPSTREAM) return 'La Arena no está disponible ahora mismo.';
    return 'Se perdió la conexión con la Arena.';
}

// Abre la primera URL que responda. Resuelve con el socket abierto, o con null.
function abrirSocket(urls) {
    return new Promise((resolve) => {
        let i = 0;
        let ultimoCodigo = 0;
        const intentar = () => {
            if (i >= urls.length) return resolve({ ws: null, codigo: ultimoCodigo });
            const url = urls[i++];
            let resuelto = false;
            let ws;
            try {
                ws = new WebSocket(url);
            } catch {
                return intentar();
            }
            ws.binaryType = 'arraybuffer';
            ws.onopen = () => { resuelto = true; resolve({ ws, codigo: 0 }); };
            ws.onerror = () => { /* el cierre que viene detrás es quien decide */ };
            ws.onclose = (ev) => {
                if (resuelto) return;
                ultimoCodigo = ev.code;
                // Un rechazo con motivo (sesión, límite, proveedor) no se
                // reintenta en la otra URL: la respuesta sería la misma.
                if ([WS_UNAUTHORIZED, WS_POLICY, WS_UPSTREAM].includes(ev.code)) {
                    return resolve({ ws: null, codigo: ev.code });
                }
                intentar();
            };
        };
        intentar();
    });
}

// ── EL WORKLET ────────────────────────────────────────────────────────────────
// Convierte Float32 a Int16 y remuestrea si hace falta.
//
// `pos` es un flotante que SE CONSERVA entre bloques. Sin eso, cada bloque de
// 128 muestras empezaría en cero y el error de redondeo se acumularía como un
// chasquido cada pocos milisegundos.
const CODIGO_WORKLET = `
class CapturaPCM extends AudioWorkletProcessor {
    constructor(opciones) {
        super();
        this.ratio = opciones.processorOptions.ratio;
        this.pos = 0;
        this.acumulado = [];
    }
    process(entradas) {
        const canal = entradas[0] && entradas[0][0];
        if (!canal) return true;
        while (this.pos < canal.length) {
            const m = Math.max(-1, Math.min(1, canal[this.pos | 0]));
            this.acumulado.push(m < 0 ? m * 0x8000 : m * 0x7fff);
            this.pos += this.ratio;
        }
        this.pos -= canal.length;
        if (this.acumulado.length >= ${MUESTRAS_POR_ENVIO}) {
            this.port.postMessage(Int16Array.from(this.acumulado));
            this.acumulado = [];
        }
        return true;
    }
}
registerProcessor('captura-pcm', CapturaPCM);
`;

export function useArenaVoice() {
    const [estado, setEstado] = useState('listo');
    const [mision, setMision] = useState(null);
    const [restante, setRestante] = useState(0);
    const [misTurnos, setMisTurnos] = useState([]);
    const [susTurnos, setSusTurnos] = useState([]);   // lo que va diciendo Spacy
    const [sugerencias, setSugerencias] = useState([]);  // el andamio, si se pidió
    const [hablando, setHablando] = useState(false);   // el personaje suena
    const [oyendo, setOyendo] = useState(false);       // el alumno habla
    const [veredicto, setVeredicto] = useState(null);
    const [error, setError] = useState('');

    const wsRef = useRef(null);
    const streamRef = useRef(null);
    const ctxEntradaRef = useRef(null);
    const ctxSalidaRef = useRef(null);
    const nodoRef = useRef(null);
    const fuentesRef = useRef(new Set());
    const siguienteRef = useRef(0);
    const relojRef = useRef(null);
    const vigiaRef = useRef(null);     // corta la espera si la misión no llega
    const cerradoRef = useRef(false);

    // ── SOLTARLO TODO ────────────────────────────────────────────────────────
    // Un micro que se queda abierto deja el punto rojo en la pestaña y el alumno
    // cree que se le sigue grabando. Se cierra siempre, pase lo que pase.
    const soltar = useCallback(() => {
        if (relojRef.current) { clearInterval(relojRef.current); relojRef.current = null; }
        if (vigiaRef.current) { clearTimeout(vigiaRef.current); vigiaRef.current = null; }
        try { nodoRef.current?.disconnect(); } catch { /* ya estaba suelto */ }
        nodoRef.current = null;
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* idem */ }
        streamRef.current = null;
        try { ctxEntradaRef.current?.close(); } catch { /* idem */ }
        ctxEntradaRef.current = null;
        fuentesRef.current.forEach((f) => { try { f.stop(); } catch { /* ya terminó */ } });
        fuentesRef.current.clear();
    }, []);

    const callar = useCallback(() => {
        fuentesRef.current.forEach((f) => { try { f.stop(); } catch { /* ya terminó */ } });
        fuentesRef.current.clear();
        siguienteRef.current = 0;
        setHablando(false);
    }, []);

    // ── EL ALTAVOZ ───────────────────────────────────────────────────────────
    // Llega PCM crudo a 24 kHz en trozos. Se encadenan con un cursor de tiempo:
    // sin él, cada trozo empezaría "ahora" y se solaparían unos con otros.
    const reproducir = useCallback((buffer) => {
        const ctx = ctxSalidaRef.current;
        if (!ctx || ctx.state === 'closed') return;
        const enteros = new Int16Array(buffer);
        if (!enteros.length) return;

        const flotantes = new Float32Array(enteros.length);
        for (let i = 0; i < enteros.length; i++) flotantes[i] = enteros[i] / 32768;

        const audio = ctx.createBuffer(1, flotantes.length, SALIDA_HZ);
        audio.copyToChannel(flotantes, 0);
        const fuente = ctx.createBufferSource();
        fuente.buffer = audio;
        fuente.connect(ctx.destination);

        const ahora = ctx.currentTime;
        if (siguienteRef.current < ahora + COLCHON_S) siguienteRef.current = ahora + COLCHON_S;
        fuente.start(siguienteRef.current);
        siguienteRef.current += audio.duration;

        fuentesRef.current.add(fuente);
        setHablando(true);
        fuente.onended = () => {
            fuentesRef.current.delete(fuente);
            if (fuentesRef.current.size === 0) setHablando(false);
        };
    }, []);

    // ── SALIR ────────────────────────────────────────────────────────────────
    // Se manda "stop" y NO se cierra el socket: el veredicto viene después, y
    // colgar aquí sería quedarse sin el recibo justo al final de la misión.
    const salir = useCallback(() => {
        if (cerradoRef.current) return;
        cerradoRef.current = true;
        if (relojRef.current) { clearInterval(relojRef.current); relojRef.current = null; }
        if (vigiaRef.current) { clearTimeout(vigiaRef.current); vigiaRef.current = null; }
        vozEnCurso(false);
        setEstado('cerrando');

        // Se corta el micro pero NO el socket: el veredicto viene después, y
        // colgar aquí sería quedarse sin el recibo justo al final.
        try { nodoRef.current?.disconnect(); } catch { /* ya estaba suelto */ }
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* idem */ }
        try { wsRef.current?.send('stop'); } catch { /* el socket ya no está */ }

        // El veredicto lo escribe una IA y puede tardar. Si a los 20 segundos no
        // ha llegado, se deja de esperar: mejor decirle al alumno que su recibo
        // no salió que dejarle mirando un "Cerrando..." eterno, que es la misma
        // pantalla muerta de la que venimos.
        vigiaRef.current = setTimeout(() => {
            vigiaRef.current = null;
            setEstado((actual) => {
                if (actual !== 'cerrando') return actual;
                soltar();
                try { wsRef.current?.close(); } catch { /* ya cerrado */ }
                setError('No llegó tu resultado a tiempo. La conversación sí contó.');
                return 'error';
            });
        }, 20000);
    }, [soltar]);

    // ── ENTRAR ───────────────────────────────────────────────────────────────
    const entrar = useCallback(async (config) => {
        setError('');
        setVeredicto(null);
        setMisTurnos([]); setSusTurnos([]); setSugerencias([]);
        cerradoRef.current = false;
        vozEnCurso(true);
        setEstado('conectando');

        // El micro PRIMERO, y no es un detalle de orden: si el alumno deniega el
        // permiso después de conectar, la misión ya cuenta contra su límite (6
        // por minuto) sin haber dicho una palabra.
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1,
                },
            });
        } catch {
            vozEnCurso(false);
            setEstado('error');
            setError('Necesito el micrófono para la misión. Actívalo y vuelve a intentarlo.');
            return;
        }
        streamRef.current = stream;

        // ⚠️ EL ORDEN DE AQUÍ ABAJO ES LO QUE HACE QUE LA MISIÓN ARRANQUE.
        //
        // Primero se monta TODO el audio, después se abre el socket, y el
        // manejador de mensajes se engancha ANTES de mandar la configuración.
        //
        // Al revés se pierde la misión: el backend contesta `{"tipo":"mision"}`
        // en cuanto recibe la configuración, y si en ese instante todavía se
        // está cargando el worklet, el evento llega sin nadie escuchando y el
        // navegador lo tira. No hay error, no hay cierre: la pantalla se queda
        // en "Spacy se está poniendo el disfraz" para siempre. Pasó.

        // ── El altavoz ──
        const CtxAudio = window.AudioContext || window.webkitAudioContext;
        const ctxSalida = new CtxAudio();
        ctxSalidaRef.current = ctxSalida;
        try { await ctxSalida.resume(); } catch { /* algunos ya lo tienen activo */ }

        // ── El micrófono ──
        // Se pide el contexto a 16 kHz para que remuestree el navegador, que lo
        // hace mejor que nosotros. Si lo ignora —pasa en algún Safari— el
        // worklet remuestrea con el ratio real, que por eso se calcula aquí.
        let ctxEntrada;
        try {
            ctxEntrada = new CtxAudio({ sampleRate: ENTRADA_HZ });
        } catch {
            ctxEntrada = new CtxAudio();
        }
        ctxEntradaRef.current = ctxEntrada;
        try { await ctxEntrada.resume(); } catch { /* idem */ }

        const ratio = Math.max(1, ctxEntrada.sampleRate / ENTRADA_HZ);
        const blob = new Blob([CODIGO_WORKLET], { type: 'application/javascript' });
        const urlWorklet = URL.createObjectURL(blob);
        try {
            await ctxEntrada.audioWorklet.addModule(urlWorklet);
        } catch {
            URL.revokeObjectURL(urlWorklet);
            soltar();
            vozEnCurso(false);
            setEstado('error');
            setError('Tu navegador no puede capturar audio para la Arena. Prueba con Chrome.');
            return;
        }
        URL.revokeObjectURL(urlWorklet);

        const origen = ctxEntrada.createMediaStreamSource(stream);
        const nodo = new AudioWorkletNode(ctxEntrada, 'captura-pcm', {
            processorOptions: { ratio },
        });
        nodoRef.current = nodo;
        nodo.port.onmessage = (ev) => {
            // `send` con un TypedArray es síncrono y conserva el orden. Aquí no
            // hay promesas de por medio a propósito: desordenar el PCM le
            // rompería el oído al agente.
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                try { wsRef.current.send(ev.data.buffer); } catch { /* se pierde un trozo */ }
            }
        };
        // El worklet NO se conecta al altavoz: devolvería la voz del alumno por
        // los cascos con retardo, que es justo lo que bloquea a cualquiera que
        // intente hablar. Un AudioWorkletNode sin destino sigue procesando.
        origen.connect(nodo);

        // ── Ahora sí: el socket ──
        const { ws, codigo } = await abrirSocket(urlsDelSocket());
        if (!ws) {
            soltar();
            vozEnCurso(false);
            setEstado('error');
            setError(describirCierre(codigo));
            return;
        }
        wsRef.current = ws;

        // ── Lo que llega ──
        ws.onmessage = (ev) => {
            if (ev.data instanceof ArrayBuffer) return reproducir(ev.data);
            let d;
            try { d = JSON.parse(ev.data); } catch { return; }

            if (d.tipo === 'mision') {
                if (vigiaRef.current) { clearTimeout(vigiaRef.current); vigiaRef.current = null; }
                setMision(d);
                setRestante(d.segundos || 120);
                setEstado('enVivo');
                relojRef.current = setInterval(() => {
                    setRestante((s) => {
                        if (s <= 1) { salir(); return 0; }
                        return s - 1;
                    });
                }, 1000);
            } else if (d.tipo === 'dijo') {
                // Los dos lados, en listas separadas: el alumno necesita ver si
                // se le entendió, y Spacy se pinta arriba para poder seguir la
                // conversación cuando una palabra se escapa al oído.
                //
                // Deepgram manda cada turno YA CERRADO, no palabra a palabra.
                // Así que el texto aparece cuando el turno termina, no mientras
                // se pronuncia: es lo máximo que da el protocolo del agente.
                if (!d.texto) return;
                if (d.quien === 'user') setMisTurnos((t) => [...t, d.texto]);
                else setSusTurnos((t) => [...t, d.texto]);
            } else if (d.tipo === 'sugerencias') {
                setSugerencias(d.opciones || []);
            } else if (d.tipo === 'UserStartedSpeaking') {
                setOyendo(true);
                callar();     // cortarle al personaje: eso es conversar
                // ⚠️ AQUÍ NO SE BORRA EL ANDAMIO.
                //
                // Antes se quitaba al detectar la primera sílaba, y el efecto
                // era el contrario del buscado: el alumno empezaba a leer la
                // sugerencia, se le desaparecía a media frase y se quedaba
                // colgado. Justo a quien más falta le hace.
                //
                // Se queda puesto mientras habla y solo lo sustituyen las
                // siguientes, que llegan cuando el personaje ya ha contestado.
            } else if (d.tipo === 'AgentAudioDone') {
                setOyendo(false);
            } else if (d.tipo === 'veredicto') {
                setVeredicto(d);
                setEstado('veredicto');
                soltar();
                try { ws.close(); } catch { /* ya cerrado */ }
            }
        };

        ws.onclose = (ev) => {
            soltar();
            setEstado((actual) => {
                if (actual === 'veredicto') return actual;
                // Cerrar sin recibo es un fallo, y se dice. Callarlo dejaría al
                // alumno mirando una pantalla que no avanza.
                setError(describirCierre(ev.code));
                return 'error';
            });
        };

        // ── EL VIGILANTE ─────────────────────────────────────────────────────
        // Si la misión no llega, el alumno se queda mirando a Spacy ponerse un
        // disfraz que no acaba nunca. Un fallo así no debe depender de que él
        // sepa recargar la página: a los 15 segundos se cuenta lo que pasa y se
        // le ofrece reintentar.
        vigiaRef.current = setTimeout(() => {
            vigiaRef.current = null;
            setEstado((actual) => {
                if (actual !== 'conectando') return actual;
                soltar();
                try { ws.close(); } catch { /* ya cerrado */ }
                setError('Spacy no llegó a la Arena. Inténtalo otra vez.');
                return 'error';
            });
            // 30 s, no 15. El backend ESCRIBE el escenario con una llamada a la
            // IA (hasta 18 s de tope) antes de mandar la misión. Con el margen
            // anterior el navegador se rendía justo cuando la situación estaba
            // a punto de llegar, y el alumno veía un error donde no lo había.
        }, 30000);

        // Y la configuración se manda LA ÚLTIMA, con todo escuchando ya.
        try {
            ws.send(JSON.stringify({
                nivel: config?.nivel || 'B1',
                contexto: config?.contexto || '',
                objetivo: config?.objetivo || '',
                semilla: config?.semilla,
                asistido: !!config?.asistido,
                modo: config?.modo || 'contextual',
            }));
        } catch {
            soltar();
            vozEnCurso(false);
            setEstado('error');
            setError('No se pudo abrir la misión.');
        }
    }, [reproducir, callar, salir, soltar]);

    // Si el alumno cambia de pantalla a mitad de misión, el micro se cierra.
    useEffect(() => () => {
        // Cerraba los sockets pero se olvidaba de esto, y la señal apaga TODOS
        // los sonidos de interfaz de la aplicacion.
        vozEnCurso(false);
        soltar();
        try { wsRef.current?.close(); } catch { /* ya cerrado */ }
        try { ctxSalidaRef.current?.close(); } catch { /* idem */ }
    }, [soltar]);

    return {
        estado, mision, restante, misTurnos, susTurnos, sugerencias,
        hablando, oyendo, veredicto, error, entrar, salir,
    };
}
